import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BackendDeletePayloadTypes,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendSetPayloadTypes,
  BackendUpdatePayloadTypes,
} from '../backend.model';
import { Collections, SearchCollections } from '../collections.model';
import {
  CollectionActions,
  DeleteRequestPayload,
  PushRequestPayload,
  SetRequestPayload,
  UpdateRequestPayload,
} from './actions';
import { ApiFetchResponse, ApiHttp, ApiPostResponse } from './api-http.service';
import { assertNever, isDefined } from './helpers';
import { pluckItemFromRawStore } from './store.helpers';
import {
  Path,
  RawQuery,
  Reference,
  SearchQuery,
  StoreState,
} from './store.model';

export interface Params {
  [key: string]:
    | string
    | number
    | ReadonlyArray<number | undefined>
    | ReadonlyArray<string | undefined>
    | undefined;
}

export interface BackendFetchRequest {
  endpoint: string;
  params?: Params;
  isGaf?: boolean;
}

type FetchRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  ids: string[] | undefined,
  query?: RawQuery<Collections[K]>,
) => BackendFetchRequest);

export interface BackendPushRequest<K extends keyof Collections> {
  payload: BackendPushPayloadTypes[K];
  endpoint: string;
  params?: Params;
  isGaf?: boolean;
  asFormData?: boolean;
}

type PushRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Collections[K],
  extra?: { [index: string]: string | number },
) => BackendPushRequest<K>);

export interface BackendSetRequest<K extends keyof Collections> {
  payload: BackendSetPayloadTypes[K];
  endpoint: string;
  params?: Params;
  isGaf?: boolean;
  asFormData?: boolean;
}

type SetRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Collections[K],
) => BackendSetRequest<K>);

export interface BackendUpdateRequest<K extends keyof Collections> {
  payload: BackendUpdatePayloadTypes[K];
  endpoint: string;
  params?: Params;
  isGaf?: boolean;
  method: 'POST' | 'PUT';
}

type UpdateRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  entity: Partial<Collections[K]>,
  originalEntity: Collections[K],
) => BackendUpdateRequest<K>);

export interface BackendDeleteRequest<K extends keyof Collections> {
  payload: BackendDeletePayloadTypes[K];
  endpoint: string;
  params?: Params;
  isGaf?: boolean;
  method: 'POST' | 'PUT' | 'DELETE';
}

type DeleteRequestFactory<K extends keyof Collections> = ((
  authUid: string,
  id: string | number,
) => BackendDeleteRequest<K>);

export interface Backend<K extends keyof Collections> {
  fetch?: FetchRequestFactory<K>;
  push?: PushRequestFactory<K>;
  set?: SetRequestFactory<K>;
  update?: UpdateRequestFactory<K>;
  remove?: DeleteRequestFactory<K>;
}

export interface BackendSearch {
  search: ((query: SearchQuery) => BackendFetchRequest);
}

export type BackendConfigs = { [K in keyof Collections]?: Backend<K> } &
  { [K in keyof SearchCollections]?: BackendSearch };

@Injectable()
export class StoreBackend {
  private backendConfigs: BackendConfigs = {};

  constructor(private store: Store<StoreState>, private apiHttp: ApiHttp) {}

  fetch<K extends keyof Collections>(
    ref: Reference<K>,
  ): Observable<ApiFetchResponse<K>> {
    const { path, query } = ref;
    const type = path.collection;

    return Observable.of((<Backend<K>>this.backendConfigs[type]).fetch).pipe(
      filter(isDefined),
      map(fetch => fetch(path.authUid, path.ids, query)),
      switchMap(request => this.apiHttp.fetch(request, query)),
    );
  }

  search<K extends keyof SearchCollections>(
    key: K,
    query: Observable<SearchQuery>,
  ): Observable<BackendFetchTypes[K]> {
    return query.pipe(
      map(q => (<BackendSearch>this.backendConfigs[key]).search(q)),
      switchMap(request => this.apiHttp.fetch(request)),
      map(
        response =>
          <BackendFetchTypes[K]>(response.status === 'success'
            ? response.result
            : []),
      ), // FIXME: Error handling?
    );
  }

  push<K extends keyof Collections>(
    ref: Reference<K>,
    object: Collections[K],
    extra?: { [index: string]: string | number },
  ): Observable<boolean> {
    const { path, query } = ref;
    const type = path.collection;

    return Observable.of((<Backend<K>>this.backendConfigs[type]).push).pipe(
      filter(isDefined),
      map(push => {
        const request = push(path.authUid, object, extra);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_PUSH`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request }) => this.apiHttp.post(request),
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions({ type: 'PUSH', payload }, path, query, result),
      ),
    );
  }

  set<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    object: Collections[K],
  ): Observable<boolean> {
    const { path, query } = ref;
    const type = path.collection;

    return Observable.of((<Backend<K>>this.backendConfigs[type]).set).pipe(
      filter(isDefined),
      withLatestFrom(this.store),
      map(([set, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = set(path.authUid, object);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_SET`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => this.apiHttp.post(request),
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>({ type: 'SET', payload }, path, query, result),
      ),
    );
  }

  update<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
    object: Partial<Collections[K]>,
  ): Observable<boolean> {
    const { path } = ref;
    const type = path.collection;

    return Observable.of((<Backend<K>>this.backendConfigs[type]).update).pipe(
      filter(isDefined),
      withLatestFrom(this.store),
      map(([update, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = update(path.authUid, object, originalObject);
        return {
          request,
          payload: {
            type,
            ref,
            object,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_UPDATE`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => {
          switch (request.method) {
            case 'POST':
              return this.apiHttp.post(request);
            case 'PUT':
              return this.apiHttp.put(request);
            default:
              return assertNever(request.method);
          }
        },
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>(
          { type: 'UPDATE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  delete<K extends keyof Collections>(
    ref: Reference<K>,
    id: number | string,
  ): Observable<boolean> {
    const { path } = ref;
    const type = path.collection;

    return Observable.of((<Backend<K>>this.backendConfigs[type]).remove).pipe(
      filter(isDefined),
      withLatestFrom(this.store),
      map(([del, storeState]) => {
        const originalObject = pluckItemFromRawStore(storeState, path, id);
        if (originalObject === undefined) {
          throw new Error('Missing original object');
        }
        const request = del(path.authUid, id);
        return {
          request,
          payload: {
            type,
            ref,
            rawRequest: request.payload,
            originalObject,
          },
        };
      }),
      tap(({ payload }) => {
        const action: CollectionActions<K> = { type: `API_DELETE`, payload };
        this.store.dispatch(action);
      }),
      switchMap(
        ({ request, payload }) => {
          switch (request.method) {
            case 'POST':
              return this.apiHttp.post(request);
            case 'PUT':
              return this.apiHttp.put(request);
            case 'DELETE':
              return this.apiHttp.delete(request);
            default:
              return assertNever(request.method);
          }
        },
        ({ payload }, result) => ({ payload, result }),
      ),
      map(({ payload, result }) =>
        this.sendActions<K>(
          { type: 'DELETE', payload },
          payload.ref.path,
          payload.ref.query,
          result,
        ),
      ),
    );
  }

  private sendActions<K extends keyof Collections>(
    baseAction:
      | { type: 'PUSH'; payload: PushRequestPayload<K> }
      | { type: 'SET'; payload: SetRequestPayload<K> }
      | { type: 'UPDATE'; payload: UpdateRequestPayload<K> }
      | { type: 'DELETE'; payload: DeleteRequestPayload<K> },
    path: Path<K>,
    query: RawQuery<Collections[K]> | undefined,
    data: ApiPostResponse<K>,
  ) {
    switch (data.status) {
      case 'success': {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_SUCCESS',
                payload: {
                  type: baseAction.payload.type,
                  ref: baseAction.payload.ref,
                  object: baseAction.payload.object,
                  rawRequest: baseAction.payload.rawRequest,
                  result: data.result,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_SUCCESS',
                  payload: {
                    type: baseAction.payload.type,
                    ref: baseAction.payload.ref,
                    object: baseAction.payload.object,
                    originalObject: baseAction.payload.originalObject,
                    rawRequest: baseAction.payload.rawRequest,
                    result: data.result,
                  },
                }
              : baseAction.type === 'UPDATE'
                ? {
                    type: 'API_UPDATE_SUCCESS',
                    payload: {
                      type: baseAction.payload.type,
                      ref: baseAction.payload.ref,
                      object: baseAction.payload.object,
                      originalObject: baseAction.payload.originalObject,
                      rawRequest: baseAction.payload.rawRequest,
                      result: data.result,
                    },
                  }
                : {
                    type: 'API_DELETE_SUCCESS',
                    payload: {
                      type: baseAction.payload.type,
                      ref: baseAction.payload.ref,
                      originalObject: baseAction.payload.originalObject,
                      rawRequest: baseAction.payload.rawRequest,
                      result: data.result,
                    },
                  };
        this.store.dispatch(action);
        return true;
      }
      case 'error': {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: {
                  ...baseAction.payload,
                  message: data.message,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_ERROR',
                  payload: { ...baseAction.payload, message: data.message },
                }
              : baseAction.type === 'UPDATE'
                ? {
                    type: 'API_UPDATE_ERROR',
                    payload: { ...baseAction.payload, message: data.message },
                  }
                : {
                    type: 'API_DELETE_ERROR',
                    payload: { ...baseAction.payload, message: data.message },
                  };
        this.store.dispatch(action);
        return false;
      }
      default: {
        const action: CollectionActions<K> =
          baseAction.type === 'PUSH'
            ? {
                type: 'API_PUSH_ERROR',
                payload: {
                  ...baseAction.payload,
                  message: 'Unexpected API response',
                  error: data,
                },
              }
            : baseAction.type === 'SET'
              ? {
                  type: 'API_SET_ERROR',
                  payload: {
                    ...baseAction.payload,
                    message: 'Unexpected API response',
                  },
                }
              : baseAction.type === 'UPDATE'
                ? {
                    type: 'API_UPDATE_ERROR',
                    payload: {
                      ...baseAction.payload,
                      message: 'Unexpected API response',
                    },
                  }
                : {
                    type: 'API_DELETE_ERROR',
                    payload: {
                      ...baseAction.payload,
                      message: 'Unexpected API response',
                    },
                  };
        this.store.dispatch(action);
        return false;
      }
    }
  }

  addFeature<K extends keyof Collections>(
    collectionName: K,
    requestFactory: Backend<K>,
  ): void;

  addFeature<K extends keyof SearchCollections>(
    collectionName: K,
    requestFactory: BackendSearch,
  ): void;

  addFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    requestFactory: any,
  ): void {
    this.backendConfigs[collectionName] = requestFactory;
  }
}

export const BACKEND_COLLECTIONS = new InjectionToken<string[]>(
  'Backend Collections',
);

export const BACKEND_CONFIGS = new InjectionToken<SearchConfigsProvider>(
  'Backend Configs',
);

type BackendConfigFactory<K extends keyof Collections> = (() => Backend<K>);

type BackendSearchConfigFactory = (() => BackendSearch);

export type SearchConfigsProvider = Backend<keyof Collections>[] &
  BackendSearch[];

@NgModule({})
export class BackendRootModule {}

@NgModule({})
export class BackendFeatureModule<
  K extends keyof (Collections & SearchCollections)
> {
  constructor(
    storeBackend: StoreBackend,
    @Inject(BACKEND_COLLECTIONS) backendCollections: K[],
    @Inject(BACKEND_CONFIGS) backendConfigs: SearchConfigsProvider,
  ) {
    backendCollections.map((collectionName, index) => {
      storeBackend.addFeature(<any>collectionName, backendConfigs[index]); // FIXME: any type
    });
  }
}

@NgModule({})
export class BackendModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: BackendRootModule,
      providers: [StoreBackend],
    };
  }

  static forFeature<K extends keyof Collections>(
    collectionName: K,
    configFactory: BackendConfigFactory<K>,
  ): ModuleWithProviders;

  static forFeature<K extends keyof SearchCollections>(
    collectionName: K,
    configFactory: BackendSearchConfigFactory,
  ): ModuleWithProviders;

  static forFeature<K extends keyof (Collections & SearchCollections)>(
    collectionName: K,
    configFactory: any,
  ): ModuleWithProviders {
    return {
      ngModule: BackendFeatureModule,
      providers: [
        {
          provide: BACKEND_COLLECTIONS,
          multi: true,
          useValue: collectionName,
        },
        {
          provide: BACKEND_CONFIGS,
          multi: true,
          useFactory: configFactory,
        },
      ],
    };
  }
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/core/backend.ts